import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../../../routes/routes.paths";
import useUserStore from "../../../../state-management/useUserStore";
import { APP_CONFIG, CustomizedTooltip } from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { useGetCompanyPayrollRuns } from "../../../../services/payroll.service";
import { useManageTableWithSearchParams } from "../../../../helpers/useManageTable";
import { PayrollRuns, PayrollRunStatus } from "../../../../models/payroll.dto";
import { Popover, Typography } from "@mui/material";
import PayrollRunModalForm from "../modals/PayrollRunModalForm";
import { useGetAllowance } from "../../../../services/allowance.service";
import { AllowanceOrderBy } from "../../../../models/allowance.dto";
import { useGetBenefit } from "../../../../services/benefit.service";
import { BenefitOrderBy } from "../../../../models/benefit.dto";
import { useGetBonus } from "../../../../services/bonus.service";
import { BonusOrderBy } from "../../../../models/bonus.dto";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import { VscRunAll } from "react-icons/vsc";
import { MdOutlineReplay } from "react-icons/md";
import { MdDoNotDisturb } from "react-icons/md";
import { FiUpload } from "react-icons/fi";
import PayrollRunActionModal from "../modals/PayrollRunActionModal";
import { useGetBonusGroup } from "../../../../services/bonus-group.service";
import { BonusGroupOrderBy } from "../../../../models/bonus-group.dto";
import {
  PermissionChecker,
  PERMISSIONS,
} from "../../../../helpers/usePermissionManager";
import {
  companyPayrollRunColumns,
  companyPayrollRunColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";

type ActionMode = "RUN_PAYROLL" | "APPROVE_PAYROLL";

const CompanyPayrollRuns = () => {
  const navigate = useNavigate();
  const { currentCompany } = useCompanyStore();
  const { currentUser } = useUserStore();
  const userOrganization = currentUser.user.organization;
  const { setFormData } = useEditFormStore();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openCreatePayrollModal, setOpenCreatePayrollModal] = useState(false);
  const [openPayrollRunActionModal, setOpenPayrollRunActionModal] =
    useState(false);
  const [payrollActionMode, setPayrollActionMode] =
    useState<ActionMode>("RUN_PAYROLL");
  const [isEditForm, setIsEditForm] = useState(false);
  const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
    useColumnVisibilityModel(companyPayrollRunColumnVisibility);

  const { tableAPIRef, tableData, isLoading, pageCount, setTableData } =
    useManageTableWithSearchParams<PayrollRuns>({
      fetchHook: useGetCompanyPayrollRuns,
      options: {
        company_id: currentCompany?.id,
        orderBy: undefined,
      },
    });

  // Fetched data for select fields requiring data from database
  const { data: payPeriods } = useGetPayPeriod({
    companyId: currentCompany?.id,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: "createdAt:desc" as any,
  });
  const { data: employees } = useGetEmployee({
    companyId: currentCompany?.id,
    limit: APP_CONFIG.PAGE_LIMIT,
    status: "ACTIVE",
    orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
  });
  const { data: employeeBands } = useGetEmployeeBand({
    companyId: currentCompany?.id,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: EmployeeBandOrderBy.NAME_ASC,
  });
  const { data: allowances } = useGetAllowance({
    companyId: currentCompany?.id,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: AllowanceOrderBy.NAME_ASC,
  });
  const { data: benefits } = useGetBenefit({
    companyId: currentCompany?.id,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: BenefitOrderBy.NAME_ASC,
  });
  const { data: bonuses } = useGetBonus({
    companyId: currentCompany?.id,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: BonusOrderBy.NAME_ASC,
    groupId: 0,
  });
  const { data: bonusGroups } = useGetBonusGroup(currentCompany?.id as number, {
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: BonusGroupOrderBy.NAME_ASC,
  });

  const handleOpenModal = () => {
    setIsEditForm(false);
    setOpenCreatePayrollModal(true);
  };

  // Free Tier accounts can only run payroll for a single employee. Notify
  // them once when they land on this page, with an inline upgrade link —
  // this does not block the run action itself.
  useEffect(() => {
    const isFreeTier =
      Number(userOrganization?.subsciptionTier?.priceAmount) === 0;

    if (!isFreeTier) return;

    toast.info(
      <div>
        <p>
          You're on the <strong>Free Tier</strong>, which only supports running
          payroll for 1 employee.
        </p>
        <button
          onClick={() => {
            toast.dismiss();
            navigate(PATHS.CREATE_PAYROLL_COMPANY + "?upgradeRequired=true");
          }}
          className="text-primary-blue font-semibold underline mt-1"
        >
          Upgrade Account
        </button>
      </div>,
      { autoClose: 8000, toastId: "free-tier-payroll-notice" },
    );
  }, []);

  const additionalColumns = [
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 160,
      flex: 1,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        const status = params.row.status as PayrollRunStatus;
        const isApproved = params.row.isApproved;
        return (
          <div
            className={`flex items-center text-xl ${
              !isApproved && "text-primary-blue"
            }`}
          >
            <PermissionChecker
              permissions={[PERMISSIONS.PAYROLL_WRITE]}
              placement="left"
            >
              <CustomizedTooltip
                title={
                  isApproved
                    ? "Cannot re-run payroll because the payslips have been published"
                    : status === PayrollRunStatus.SUBMITTED
                      ? "Run Payroll"
                      : status === PayrollRunStatus.PROCESSING
                        ? "Recent run still processing"
                        : "Re-run Payroll"
                }
                placement="top"
                arrow
              >
                <Typography>
                  <button
                    className="py-2 px-4 rounded-full flex items-center gap-3 hover:bg-gray-200"
                    onClick={() => {
                      setFormData(params.row);
                      setPayrollActionMode("RUN_PAYROLL");
                      setOpenPayrollRunActionModal(true);
                      setAnchorEl(null);
                    }}
                    disabled={
                      isApproved || status === PayrollRunStatus.PROCESSING
                    }
                  >
                    {status === PayrollRunStatus.SUBMITTED ? (
                      <VscRunAll />
                    ) : status === PayrollRunStatus.PROCESSING ? (
                      <MdDoNotDisturb />
                    ) : (
                      <MdOutlineReplay />
                    )}
                  </button>
                </Typography>
              </CustomizedTooltip>
            </PermissionChecker>

            {status === PayrollRunStatus.COMPLETED && (
              <PermissionChecker
                permissions={[PERMISSIONS.PAYSLIPS_WRITE]}
                placement="left"
              >
                <CustomizedTooltip
                  title={
                    isApproved
                      ? "Payslips already published"
                      : "Publish Payslips"
                  }
                  placement="top"
                  arrow
                >
                  <Typography>
                    <button
                      className="py-2 px-4 rounded-full flex items-center gap-3 hover:bg-gray-200"
                      onClick={() => {
                        setFormData(params.row);
                        setPayrollActionMode("APPROVE_PAYROLL");
                        setOpenPayrollRunActionModal(true);
                        setAnchorEl(null);
                      }}
                      disabled={isApproved}
                    >
                      <FiUpload />
                    </button>
                  </Typography>
                </CustomizedTooltip>
              </PermissionChecker>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mt-4 mb-8">
        <div className="flex items-center gap-3">
          <TableFilter fields={[]} />
          <ManageTableColumns
            columns={companyPayrollRunColumns}
            defaultVisibilityModel={companyPayrollRunColumnVisibility}
            visibilityModel={columnVisibilityModel}
            setVisibilityModel={setColumnVisibilityModel}
            tableApiRef={tableAPIRef}
          />
        </div>
        <PermissionChecker permissions={[PERMISSIONS.PAYROLL_WRITE]}>
          <OrangeButton
            title="Create Payroll Run"
            className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
            onClick={handleOpenModal}
          />
        </PermissionChecker>
      </div>
      <TanStackEmployeeTable
        ref={tableAPIRef}
        tableId={TableId.PAYROLL_RUNS}
        data={tableData}
        columns={companyPayrollRunColumns}
        columnVisibility={columnVisibilityModel}
        onColumnVisibilityChange={setColumnVisibilityModel}
        loading={isLoading}
        pageCount={pageCount} // 👈 add this
        onRowClick={(row, event) => {
          if (row.status === PayrollRunStatus.COMPLETED && !row.isApproved) {
            setFormData(row);
            setAnchorEl(event.currentTarget);
          }
        }}
        initialColumnVisibility={companyPayrollRunColumnVisibility}
        pageSize={10}
        meta={{
          onRunPayroll: (row: any, event: any) => {
            setFormData(row);
            setPayrollActionMode("RUN_PAYROLL");
            setOpenPayrollRunActionModal(true);
          },
          onPublishPayslips: (row: any, event: any) => {
            setFormData(row);
            setPayrollActionMode("APPROVE_PAYROLL");
            setOpenPayrollRunActionModal(true);
          },
        }}
      />
      <Popover
        id={anchorEl ? "simple-popover" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            width: "auto",
            borderRadius: 8,
            display: "flex",
            justifyContent: "center",
          },
        }}
      >
        <div className="flex flex-col justify-center p-3 text-secondary-darkgray">
          <div
            onClick={() => {
              setPayrollActionMode("APPROVE_PAYROLL");
              setOpenPayrollRunActionModal(true);
              setAnchorEl(null);
            }}
            className="table-action-popover"
          >
            <FiUpload />
            <p className="text-2sm">Publish Payslips</p>
          </div>
        </div>
      </Popover>
      {openPayrollRunActionModal && (
        <PayrollRunActionModal
          openModal={openPayrollRunActionModal}
          setOpenModal={setOpenPayrollRunActionModal}
          actionMode={payrollActionMode}
        />
      )}
      {openCreatePayrollModal && (
        <PayrollRunModalForm
          open={openCreatePayrollModal}
          setOpen={setOpenCreatePayrollModal}
          title={isEditForm ? "Update Payroll Run" : "Create Payroll Run"}
          isEditForm={isEditForm}
          tableAPIRef={tableAPIRef}
          payPeriods={payPeriods?.data || []}
          employees={employees?.data || []}
          employeeBands={employeeBands?.data || []}
          allowances={allowances?.data || []}
          benefits={benefits?.data || []}
          bonuses={bonuses?.data || []}
          bonusGroups={bonusGroups?.data || []}
        />
      )}
    </>
  );
};

export default CompanyPayrollRuns;
